import { useTranslation } from 'react-i18next';
import { useLazyGetOrderEventsQuery } from '../api/booking/orderEventsApiSlice';
import {
    customerMailHint,
    readCustomerMailHistory,
} from '../flows/customerMailHistory';

/**
 * The one sentence the cancel dialog adds: what the event log knows about
 * customer mails for this booking.
 *
 * LAZY on purpose. Every booking-details view would otherwise pay for a read
 * that only matters to the few visits that end in a cancellation, and the
 * dialog is the only consumer.
 *
 * Never throws and never blocks: a failed or slow events read returns an empty
 * string, so the dialog opens exactly as it did before. A cancellation must not
 * become unavailable because a hint could not be fetched — the operator can
 * still answer the question themselves, which is what they did until now.
 */
export const useCustomerMailHint = () => {
    const { t } = useTranslation();
    const [fetchEvents] = useLazyGetOrderEventsQuery();

    return async (orderId: string): Promise<string> => {
        // `null` means "we could not read the log", which is NOT "nothing was
        // sent" — saying the latter would be a claim the data does not support,
        // and it is the claim that would talk an operator out of a mail the
        // customer needed. Passed through the same function so the distinction
        // is made in one place rather than by two callers agreeing.
        let history = null;
        if (orderId) {
            try {
                const response = await fetchEvents({ orderId }).unwrap();
                history = readCustomerMailHistory(response?.events);
            } catch {
                history = null;
            }
        }
        const hint = customerMailHint(history);
        return hint ? t(hint.messageKey, { count: hint.count }) : '';
    };
};
